import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Wallet, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const Signup = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    agreeToTerms: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.info('Under development - Signup functionality coming soon!');
  };

  const handleWalletConnect = () => {
    toast.info('Under development - Wallet connection coming soon!');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-20" style={{ background: 'var(--bg-primary)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center">
            <img src="/logo.png" alt="MatrixLaunch Logo" className="h-16 w-auto object-contain" />
          </Link>
          <h1 className="text-3xl font-semibold mt-8 mb-2" style={{ color: 'var(--text-primary)' }}>Create Your Account</h1>
          <p style={{ color: 'var(--text-muted)' }}>Join thousands of investors today</p>
        </div>

        {/* Signup Form */}
        <div className="bg-white/5 border border-white/25 backdrop-blur-xl rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} size={18} />
                <Input
                  type="text"
                  placeholder="John Doe"
                  className="pl-12 bg-white/5 placeholder:text-white/40 h-14"
                  style={{ 
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)'
                  }}
                  value={formData.fullName}
                  onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} size={18} />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  className="pl-12 bg-white/5 placeholder:text-white/40 h-14"
                  style={{ 
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)'
                  }}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  className="pl-12 pr-12 bg-white/5 placeholder:text-white/40 h-14"
                  style={{ 
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)'
                  }}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} size={18} />
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  className="pl-12 pr-12 bg-white/5 placeholder:text-white/40 h-14"
                  style={{ 
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)'
                  }}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Referral Code <span style={{ color: 'var(--text-muted)' }}>(Optional)</span>
              </label>
              <div className="relative">
                <CheckCircle className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} size={18} />
                <Input
                  type="text"
                  placeholder="Enter referral code"
                  className="pl-12 bg-white/5 placeholder:text-white/40 h-14"
                  style={{ 
                    borderColor: 'var(--border-subtle)',
                    color: 'var(--text-primary)'
                  }}
                  value={formData.referralCode}
                  onChange={(e) => setFormData({...formData, referralCode: e.target.value})}
                />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input 
                type="checkbox" 
                className="w-4 h-4 mt-1 rounded border-white/25 bg-white/5"
                checked={formData.agreeToTerms}
                onChange={(e) => setFormData({...formData, agreeToTerms: e.target.checked})}
              />
              <label className="text-sm" style={{ color: 'var(--text-muted)' }}>
                I agree to the{' '}
                <a href="/terms" className="text-brand-primary hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" className="text-brand-primary hover:underline">Privacy Policy</a>
              </label>
            </div>

            <Button type="submit" className="btn-primary w-full">
              Create Account
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: 'var(--border-subtle)' }}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4" style={{ background: 'var(--bg-primary)', color: 'var(--text-muted)' }}>Or sign up with</span>
            </div>
          </div>

          {/* Wallet Connect */}
          <Button 
            className="btn-secondary w-full flex items-center justify-center gap-2"
            onClick={handleWalletConnect}
          >
            <Wallet size={18} />
            Connect Wallet
          </Button>

          {/* Login Link */}
          <p className="text-center text-sm mt-6" style={{ color: 'var(--text-muted)' }}>
            Already have an account?{' '}
            <Link to="/login" className="text-brand-primary hover:underline font-medium">
              Login
            </Link>
          </p>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Your data is encrypted and secured with blockchain technology
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
